import { type UseApiDataOptions, useApiData } from '@repo/shared/hooks';
import { useMemo } from 'react';
import type { FareDetailsErrorResponse } from '@/api';
import {
	type FareDetailSuccessResponse,
	fetchFareDetails,
} from '@/api/fareDetails';
import type { SearchFormData } from '@/components/search-form';

type ErrorWithApiError = Error & { apiError?: FareDetailsErrorResponse };
function useApiDataWithCustomError<T>(
	fetcher: (options: { signal: AbortSignal }) => Promise<T>,
	dependencies: React.DependencyList,
	options: UseApiDataOptions<T> = {},
): {
	data: T | null;
	status: 'loading' | 'refreshing' | 'idle';
	error: FareDetailsErrorResponse | null;
} {
	const [{ data, status, error }] = useApiData(fetcher, dependencies, options);

	return useMemo(
		() => ({
			data,
			status,
			error: (error as ErrorWithApiError)?.apiError ?? null,
		}),
		[data, status, error],
	);
}

interface FareDetailHookOptions
	extends UseApiDataOptions<FareDetailSuccessResponse['data']> {
	signal?: AbortSignal;
}

interface FareDetailHookResult {
	loading: boolean;
	error: FareDetailsErrorResponse | null;
	fareDetailsResponse: FareDetailSuccessResponse['data'];
}

export function useFareDetail(
	formData: Required<SearchFormData> | null | undefined,
	options: FareDetailHookOptions = {},
): FareDetailHookResult {
	// Create fetcher function that useApiData will call
	const fetcher = useMemo(() => {
		return async ({ signal }: { signal: AbortSignal }) => {
			if (!formData) {
				return null;
			}
			const fareDetailsResponse = await fetchFareDetails(formData, signal);
			if ('errors' in fareDetailsResponse) {
				// Convert API error to Error object that useApiData expects
				const error = new Error('API Error') as ErrorWithApiError;
				error.apiError = { errors: fareDetailsResponse.errors };
				throw error;
			}

			return fareDetailsResponse.data;
		};
	}, [formData]);

	const { data, status, error } = useApiDataWithCustomError(
		fetcher,
		[formData],
		{
			...options,
			enabled: (options.enabled ?? true) && !!formData,
		},
	);

	return useMemo(
		() => ({
			loading: status === 'loading',
			error,
			fareDetailsResponse: data || ({} as FareDetailSuccessResponse['data']),
		}),
		[data, status, error],
	);
}
